import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useMemo,
} from "react";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import {
  getInitPopUp,
  getInitPopUpData,
  getTablebySql,
} from "@shared/utils/CommonUtils";
import { global } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import SnResizable from "@shared/components/SnResizable";
import { showDialog } from "@widgets/CommonDialog";
import usePanelSizes from "@shared/hooks/usePanelSizes";
import SearchPanel from "@widgets/SearchPanel"; // 조회조건 Panel
import { Button } from "@mui/material";

interface popupProps {
  selectedData?: any;
  modalRef?: any;
}

interface RowData {
  chk?: any;
  KeyField?: string;
  [key: string]: any;
}

type VariantRole = "fit" | "color" | "size";

interface CombinationDetail {
  code: string;
  name: string;
  values: Partial<Record<VariantRole, string>>;
}

const SN_MAS_BAS_REG_001_TEX_POP_001 = forwardRef(
  ({ selectedData, modalRef }: popupProps, ref) => {
    //#region Variable
    const { userInfo } = global();
    const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
      grid1: React.createRef(),
      grid2: React.createRef(),
    });

    const [columns, setColums] = useState<any>({
      grid1: [],
      grid2: [],
    });
    const [dataSources, setDataSources] = useState<any>({
      grid1: [],
      grid2: [],
    });
    const searchRef = useRef<any>(null);
    const today = new Date();
    const todayform = `${today.getFullYear()}${(today.getMonth() + 1)
      .toString()
      .padStart(2, "0")}${today.getDate().toString().padStart(2, "0")}`;
    //#endregion

    //#region Components useState
    const [formData, setFormData] = useState<any>();
    const [formControlData, setFormControlData] = useState<any[]>([]);
    const [gridFocus, setGridFocus] = useState<boolean>();
    //#endregion
    const [formReady, setFormReady] = useState(false);
    //#region grid 길이 관련
    const cd_menu = modalRef?.cd_menu || "SN_MAS_BAS_REG_001_TEX_POP_001";
    const defaultSize = { panel1width: "49.5%", panel2width: "49.5%" };
    const [sizes, onResizeStop] = usePanelSizes(cd_menu, defaultSize);
    //#endregion

    useImperativeHandle(ref, () => ({
      checkValidity: async () => {
        const modifiedRows = gridRefs.current["grid2"].current.getChange();
        let lsChk: any = "2";
        if (modifiedRows.modifiedRowcount === 0) {
          lsChk = "1";
        } else {
          lsChk = "2";
        }

        if (lsChk === "1") {
          return { flag: false, message: "변경된 내용이 없습니다." };
        }
        await onSave();
        return { flag: true };
      },

      getSelectedRow: () => [
        gridRefs.current["grid2"].current.getSelectedRows() || [],
      ],
    }));

    useEffect(() => {
      gridRefs.current["grid1"].current.selectAndFocusRow(0);
      setGridFocus(false);
      gridRefs.current["grid2"].current.selectAndFocusRow(0);
      setGridFocus(false);
    }, [gridFocus]);

    useEffect(() => {
      const fetchform = async () => {
        try {
          const rtn: any = await getInitPopUpData(
            "SN_MAS_BAS_REG_001_TEX_POP_001",
            {}
          );
          const mergedData: any = {
            ...rtn.formData,
            no_style: selectedData.no_style,
            tp_item:
              selectedData.tp_item === null || selectedData.tp_item === ""
                ? "PRD"
                : selectedData.tp_item,
          };

          setFormControlData(rtn.formControlData);
          setFormData(mergedData);
          setColums({
            grid1: rtn.grid.grid1,
            grid2: rtn.grid.grid2,
          });
          await onSearch();
        } catch (error) {}
      };
      fetchform();
    }, [userInfo]);

    //#region onSearch
    const onSearch = async () => {
      try {
        const param = {
          id: "sn_mas_bas_reg_001_tex_pop_001_01",
          params: {
            cd_firm: userInfo.cd_firm,
            tp_item:
              selectedData.tp_item === null || selectedData.tp_item === ""
                ? "PRD"
                : selectedData.tp_item,
          },
        };
        const data: any = await fnGetDataSet(param);

        if (data[0] && data[0].length > 0) {
          const strCdClasList = data[0].map((row: any) => row.cd_clas); // make array
          const strCdClas = strCdClasList.join(",");
          const noStyle = selectedData?.no_style;
          const param2 = {
            id: "sn_mas_bas_reg_001_tex_pop_001_02",
            params: {
              cd_firm: userInfo.cd_firm,
              cd_clas: strCdClas,
              no_style: noStyle,
            },
          };
          const data2: any = await fnGetDataSet(param2);
          setDataSources({
            grid1: data[0],
            grid2: data2[0],
          });
          await new Promise((resolve) => setTimeout(resolve, 50));
          setGridFocus(true);
        } else {
          gridRefs.current["grid1"].current.resetUpdate();
        }
      } catch (error) {}
    };
    //#endregion

    //region onSave
    const onSave = async () => {
      const dt: any[] = [];

      try {
        gridRefs.current["grid2"].current.stopEditing();
        const modifiedRows = gridRefs.current["grid2"].current.getChange();
        const updatedRows = modifiedRows?.update || [];

        const filteredRows = updatedRows.filter(
          (row: any) =>
            row.chk === "Y" && (row.cd_firm === "" || row.cd_firm === null)
        );

        const deleteRows = updatedRows.filter(
          (row: any) => row.chk === "N" && row.cd_firm !== null
        );

        if (filteredRows.length === 0 && deleteRows.length === 0) {
          await showDialog("alter", "저장할 선택된 행이 없습니다.");
          return;
        }
        const cdClasToNoLineMap = new Map<string, string>();
        gridRefs.current["grid1"].current.forEachFilteredNode((node: any) => {
          cdClasToNoLineMap.set(node.data.cd_clas, node.data.no_line);
        });
        const no_style = selectedData.no_style || "";
        for (const row of filteredRows) {
          if (row.chk === "Y") {
            dt.push({
              ...row,
              cd_firm: userInfo.cd_firm,
              no_style,
              no_line_config: cdClasToNoLineMap.get(row.cd_clas),
              chk: "Y",
            });
          }
        }

        const masItemDelete: any[] = [];

        if (deleteRows.length > 0) {
          const grid1Data: any[] = [];
          const grid2Data: any[] = [];

          gridRefs.current["grid1"].current.forEachFilteredNode((node: any) => {
            grid1Data.push(node.data);
          });

          gridRefs.current["grid2"].current.forEachFilteredNode((node: any) => {
            grid2Data.push(node.data);
          });

          const combinations = generateAllCombinations(
            grid1Data,
            grid2Data,
            selectedData,
            "Y"
          );
          deleteRows.forEach((node: any) => {
            masItemDelete.push({
              cd_firm: userInfo.cd_firm,
              cd_item: no_style + node.cd_flag,
            });
          });
          const noStyle = selectedData.no_style;

          const deletedFlags = new Set(
            deleteRows
              .filter((row: any) => row.chk === "N")
              .map((row: any) => row.cd_flag)
          );

          combinations.forEach((combo: string) => {
            const flag = combo.replace(noStyle, "");

            const flagParts = flag.match(/.{1,3}/g) || [];

            if (flagParts.some((part) => deletedFlags.has(part))) {
              masItemDelete.push({
                cd_firm: userInfo.cd_firm,
                cd_item: combo,
              });
            }
          });
        }
        const SaveData = {
          mas_stylel_tex: {
            insert: dt,
            delete: deleteRows,
          },
          mas_item: { delete: masItemDelete },
        };

        const sqlQuery = {
          sql: "update mas_styleh_tex set tp_item = :tp_item where cd_firm = :cd_firm and no_style = :no_style",
          cd_firm: userInfo.cd_firm,
          no_style: selectedData.no_style,
          tp_item: searchRef.current.getFormdata().tp_item,
        };
        await getTablebySql(sqlQuery);
        await fnSave("SN_MAS_BAS_REG_001_TEX_POP_001_SAVE_O1", SaveData);
        gridRefs.current["grid2"].current.resetUpdate();
      } catch (error: any) {}
    };
    //#endregion

    //#region onFocusedRowChanged
    const onFocusedRowChanged = (event: any) => {
      const selectedRow = event;
      if (selectedRow) {
        const cd_clas = selectedRow.cd_clas;
        gridRefs.current["grid2"].current.applyFilters({ cd_clas: cd_clas });
        gridRefs.current["grid2"].current.selectAndFocusRow(0);
      } else {
        return;
      }
    };
    //#endregion

    const normalizeVariantText = (value: any) =>
      `${value ?? ""}`.trim().toUpperCase();

    const getVariantRole = (
      group: any,
      fallbackIndex?: number
    ): VariantRole | undefined => {
      const cdClas = normalizeVariantText(group?.cd_clas);
      const nmClas = normalizeVariantText(group?.nm_clas);

      if (
        cdClas === "MAS166" ||
        nmClas.includes("FIT") ||
        nmClas.includes("핏")
      )
        return "fit";
      if (
        cdClas === "MAS167" ||
        nmClas.includes("COLOR") ||
        nmClas.includes("COLOUR") ||
        nmClas.includes("컬러") ||
        nmClas.includes("칼라") ||
        nmClas.includes("색상")
      )
        return "color";
      if (
        cdClas === "MAS168" ||
        nmClas.includes("SIZE") ||
        nmClas.includes("사이즈") ||
        nmClas.includes("치수")
      )
        return "size";

      if (fallbackIndex === 0) return "fit";
      if (fallbackIndex === 1) return "color";
    };

    const getCheckedFlags = (
      grid2Data: any[],
      cdClas: string,
      ynChk: "Y" | "N"
    ) =>
      grid2Data
        .filter((item) => item.cd_clas === cdClas && item.chk === ynChk)
        .map((item) => ({
          code: `${item.cd_flag ?? ""}`,
          name: `${item.nm_flag ?? ""}`,
        }))
        .filter((item) => item.code !== "");

    const getSingleCheckedRoleValue = (
      grid1Data: any[],
      grid2Data: any[],
      role: VariantRole,
      ynChk: "Y" | "N"
    ) => {
      const values = grid1Data
        .filter((group) => getVariantRole(group) === role)
        .flatMap((group) =>
          getCheckedFlags(grid2Data, group.cd_clas, ynChk).map(
            (item) => item.code
          )
        );
      const uniqueValues = Array.from(new Set(values));
      return uniqueValues.length === 1 ? uniqueValues[0] : "";
    };

    const getVariantRoleOrder = (role?: VariantRole) => {
      if (role === "fit") return 1;
      if (role === "color") return 2;
      if (role === "size") return 3;
      return 99;
    };

    const generateCombinationDetails = (
      grid1Data: any[],
      grid2Data: any[],
      selectedData: any,
      ynItemcodeFilter: "Y" | "N",
      ynChk: "Y" | "N"
    ): CombinationDetail[] => {
      const noStyle = selectedData.no_style || "";
      const groups = grid1Data
        .filter((group) => group.yn_itemcode === ynItemcodeFilter)
        .map((group, index) => ({
          role: getVariantRole(group, index),
          items: getCheckedFlags(grid2Data, group.cd_clas, ynChk),
        }))
        .filter((group) => group.items.length > 0)
        .sort(
          (a, b) => getVariantRoleOrder(a.role) - getVariantRoleOrder(b.role)
        );

      const sizeFallback = getSingleCheckedRoleValue(
        grid1Data,
        grid2Data,
        "size",
        ynChk
      );

      if (groups.length === 0) return [];

      return groups
        .reduce<CombinationDetail[]>(
          (acc, group) =>
            acc.flatMap((base) =>
              group.items.map((item) => ({
                code: `${base.code}${item.code}`,
                name: `${base.name}${item.name}`,
                values: {
                  ...base.values,
                  ...(group.role ? { [group.role]: item.code } : {}),
                },
              }))
            ),
          [{ code: noStyle, name: noStyle, values: {} }]
        )
        .map((detail) => ({
          ...detail,
          values: {
            ...detail.values,
            size: detail.values.size || sizeFallback,
          },
        }));
    };

    const generateAllCombinations = (
      grid1Data: any[],
      grid2Data: any[],
      selectedData: any,
      ynItemcodeFilter: "Y" | "N"
    ): string[] => {
      const codeMap: Record<string, string[]> = {};

      const filteredGroups = grid1Data.filter(
        (group) => group.yn_itemcode === ynItemcodeFilter
      );

      filteredGroups.forEach((group) => {
        const codes = grid2Data
          .filter((item) => item.cd_clas === group.cd_clas)
          .map((item) => item.cd_flag);

        codeMap[group.cd_clas] = codes;
      });

      const combine = (arrays: string[][]): string[] => {
        if (!arrays.length) return [];
        return arrays.reduce((a, b) =>
          a.flatMap((x) => b.map((y) => `${x}${y}`))
        );
      };

      const orderedCodeLists = filteredGroups
        .map((group) => codeMap[group.cd_clas] || [])
        .filter((arr: string[]) => arr.length > 0);

      const noStyle = selectedData.no_style || "";
      return combine(orderedCodeLists).map((combo) => `${noStyle}${combo}`);
    };

    //region onGrid1BtnsClick
    const onGrid1BtnsClick = async (id: string) => {
      try {
        if (id === "btnClick") {
          const grid1Data: any[] = [];
          const grid2Data: any[] = [];

          gridRefs.current["grid1"].current.forEachFilteredNode((node: any) => {
            grid1Data.push(node.data);
          });

          gridRefs.current["grid2"].current.forEachNode((node: any) => {
            grid2Data.push(node.data);
          });

          const noStyle = selectedData.no_style;
          const noStyleNm = selectedData.nm_style;
          const combinationDetails = generateCombinationDetails(
            grid1Data,
            grid2Data,
            selectedData,
            "Y",
            "Y"
          );
          if (combinationDetails.length === 0) return;

          //const strJoin = combinations.join(",");

          const sqlQuery = {
            sql: "select cd_item from mas_item where cd_firm = :cd_firm and dc_dummy01 = :dc_dummy01;",
            cd_firm: userInfo.cd_firm,
            dc_dummy01: noStyle,
          };

          const findExistingData = await getTablebySql(sqlQuery);

          const existingItems = new Set(
            findExistingData.map((row: any) => row.cd_item)
          );

          const newCombinationDetails = combinationDetails.filter(
            (detail) => !existingItems.has(detail.code)
          );

          if (newCombinationDetails.length === 0) {
            await showDialog("alter", "데이터가 이미 존재합니다.");
            return;
          }

          const masItemInsertList: any[] = [];
          for (const detail of newCombinationDetails) {
            const updatedRow = {
              cd_firm: userInfo.cd_firm,
              cd_item: detail.code,
              nm_item: detail.name.replace(noStyle, noStyleNm),
              dc_item_unit: "EA",
              nm_model: noStyleNm,
              dc_dummy01: noStyle,
              cd_uer_reg: userInfo.cd_user,
              cd_mgmt1: searchRef.current.getFormdata().tp_item,
              fg_vat: "11",
              yn_bf: "Y",
              yn_phn: "N",
              yn_lot: "N",
              yn_sn: "N",
              yn_use: "Y",
              yn_iqc: "N",
              yn_eqc: "N",
              yn_pqc: "N",
              yn_autorcv: "N",
              yn_autobf_prd: "N",
              yn_inqc: "N",
              yn_longterm: "N",
              fg_item: selectedData.fg_item || "",
              fg_acct: selectedData.fg_acct || "",
              tp_item: selectedData.tp_item || "",
              fg_domestic: "D",
              tp_policy: "L4L",
              tp_po: "MRP",
              dt_start: todayform,
              dt_end: "21001231",
              qt_unit_so: 1,
              qt_unit_po: 1,
              qt_max: 1,
              qt_min: 1,
              cd_dummy01: detail.values.fit || "",
              cd_dummy02: detail.values.color || "",
              cd_dummy05: detail.values.size || "",
            };
            masItemInsertList.push(updatedRow);
          }
          const SaveData = {
            mas_item: { insert: masItemInsertList },
          };
          await fnSave("SN_MAS_BAS_REG_001_TEX_POP_001_SAVE_O1", SaveData);
        }
      } catch (error: any) {}
    };
    const grids = useMemo(
      () => ({
        grid1: {
          columns: columns.grid1,
          dataSource: dataSources.grid1,
          events: {
            onFocusedRowChanged,
          },
        },
        grid2: {
          columns: columns.grid2,
          dataSource: dataSources.grid2,
        },
      }),
      [columns, dataSources]
    );
    //#region UI
    return (
      <div className="h-full flex flex-col">
        <div className="mb-1">
          <SearchPanel
            ref={searchRef}
            data={formData}
            controls={formControlData}
            popupRef={ref}
          />
        </div>
        <div className="flex w-full h-full min-w-0 space-x-1.5">
          <SnResizable
            direction={"width"}
            seq={1}
            cd_menu={cd_menu}
            defaultSize={defaultSize}
            size={sizes.panel1width}
            onResizeStop={onResizeStop}
            enable={{ right: true }}
            minWidth={130}
          >
            <SnGrid
              ref={gridRefs.current["grid1"]}
              columnConfig={grids.grid1.columns}
              rowData={grids.grid1.dataSource}
              etcProps={{
                rowSelection: "single",
                title: "규격항목",
              }}
              events={grids.grid1.events}
            />
          </SnResizable>
          <SnResizable
            direction={"width"}
            seq={2}
            cd_menu={cd_menu}
            defaultSize={defaultSize}
            size={sizes.panel2width}
            onResizeStop={onResizeStop}
            enable={{ right: true }}
            minWidth={130}
          >
            <SnGrid
              ref={gridRefs.current["grid2"]}
              columnConfig={grids.grid2.columns}
              rowData={grids.grid2.dataSource}
              etcProps={{
                rowSelection: "single",
                title: "세부규격",
              }}
            />
          </SnResizable>
        </div>
        <div className="flex justify-center gap-2 mt-4">
          <Button
            variant="contained"
            onClick={() => modalRef.current?.handleConfirm(true)}
            sx={{ width: 80 }}
          >
            저장
          </Button>
          <Button
            variant="outlined"
            onClick={() => modalRef.current?.handleConfirm(false)}
            sx={{ width: 80 }}
          >
            취소
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              onGrid1BtnsClick("btnClick");
            }}
            sx={{ width: 90 }}
          >
            코드생성
          </Button>
        </div>
      </div>
    );
  }
  //#endregion
);

export default SN_MAS_BAS_REG_001_TEX_POP_001;
